/**
 * @ken/module-messaging — frontend entry point (slice 10, design report §2).
 *
 * The route table only: every screen arrives through lazyScreen (ADR-040,
 * BND-01), so nothing of the inbox is in the first download.
 */

import { lazyScreen, type AnyRoute, type CreateModuleRoutes } from "@ken/cockpit-types";
import { createRoute } from "@tanstack/react-router";

import { readTableQuery, type TableQuery } from "./lib/table-query";

const MessagesInboxPage = lazyScreen(() => import("./routes/inbox"), "MessagesInboxPage");
const NewTextPage = lazyScreen(() => import("./routes/new-text"), "NewTextPage");
const MessagingSettingsPage = lazyScreen(() => import("./routes/settings"), "MessagingSettingsPage");
const ThreadStoryDetail = lazyScreen(() => import("./routes/thread-story"), "ThreadStoryDetail");
const MessageStoryDetail = lazyScreen(() => import("./routes/message-story"), "MessageStoryDetail");

/**
 * ADR-0251: the tabs this module puts on other modules' records, by the
 * manifest's `frontend.record_tabs[].component` name. Each body is a lazy
 * chunk, so a record page pays nothing until its Messages tab opens.
 */
export const recordTabs = {
  RecordMessagesTab: lazyScreen(() => import("./components/record/RecordMessagesTab"), "RecordMessagesTab"),
};

export type InboxTabId = "needs_a_person" | "mine" | "sent" | "failed" | "all";
export type InboxFilterId = "all" | "customers" | "suppliers" | "staff" | "unknown";

/**
 * The inbox's URL-borne state (design report §2): reload and back restore it.
 * The All table's sort and filters (D-247, T4.1) ride along as `TableQuery`.
 */
export interface InboxSearch extends TableQuery {
  tab: InboxTabId;
  filter: InboxFilterId;
  number?: string | undefined;
  q?: string | undefined;
  open?: string | undefined;
}

const TABS: readonly InboxTabId[] = ["needs_a_person", "mine", "sent", "failed", "all"];
const FILTERS: readonly InboxFilterId[] = ["all", "customers", "suppliers", "staff", "unknown"];

function str(v: unknown): string | undefined {
  return typeof v === "string" && v ? v : undefined;
}

export function validateInboxSearch(raw: Record<string, unknown>): InboxSearch {
  const rawTab = raw["tab"] === "needs" ? "needs_a_person" : raw["tab"];
  const tab = TABS.includes(rawTab as InboxTabId) ? (rawTab as InboxTabId) : "needs_a_person";
  const filter = FILTERS.includes(raw["filter"] as InboxFilterId) ? (raw["filter"] as InboxFilterId) : "all";
  const number = str(raw["number"]);
  const q = str(raw["q"]);
  const open = str(raw["open"]);
  return {
    tab,
    filter,
    ...(number ? { number } : {}),
    ...(q ? { q } : {}),
    ...(open ? { open } : {}),
    ...readTableQuery(raw),
  };
}

export const createRoutes: CreateModuleRoutes = (parentRoute: AnyRoute) => {
  const inboxRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "messages",
    validateSearch: validateInboxSearch,
    component: MessagesInboxPage,
  });
  const newTextRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "messages/new",
    component: NewTextPage,
  });
  const settingsRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "settings/integrations/messaging",
    validateSearch: (raw: Record<string, unknown>) => ({
      ...(str(raw["number"]) ? { number: str(raw["number"]) } : {}),
      ...(str(raw["section"]) ? { section: str(raw["section"]) } : {}),
    }),
    component: MessagingSettingsPage,
  });
  const threadRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "threads/$id",
    component: ThreadStoryDetail,
  });
  const messageRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "messages/$id",
    component: MessageStoryDetail,
  });
  return [inboxRoute, newTextRoute, settingsRoute, threadRoute, messageRoute];
};
